import _slicedToArray from "/vercel/path0/node_modules/.pnpm/next@12.2.0_@babel+core@7.16.0_react-dom@17.0.2_react@17.0.2/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { useCallback, useMemo, useEffect, useState } from 'react';
import { parseUnits } from 'ethers/lib/utils';
import { MaxUint256, Zero } from '@ethersproject/constants';
import { message } from "../../Message";
import { ApprovalState, useApproveCallback, useNeedReduceAllowance2Zero } from "../../../hooks/useApproval";
import { useModalToggle } from "../../../state/application/hooks";
import { ApplicationModal } from "../../../state/application/reducer";
import { useBestBuyPair, useBestBuyInputAmount, useBestBuyUserConfig } from "../../../state/dex/hooks";
import { useBestBuyAllowance } from "./useBestBuyAllowance";
import { ApprovalProgress } from "../../ApprovalModal/ApproveStatusLabel";
import { useUpdaterChainId, useNetworkValidator } from "../../../hooks";
import { useTranslation } from 'react-i18next';
import { useWeb3React } from '@web3-react/core';
import { useTokenBalance } from "../../../state/wallet/hooks";
import { FixedPointNumber } from "../../../utils/fixedPoint";
import { toBigNumberWithDecimal } from "../../../utils/numbers";
import { BEST_BUY } from '@tokenlon/sdk';
import { useAvailableTokenBalance } from "./useAvailableTokenBalance";
import { useActiveOrdersOccupiedAmount } from "./useActiveOrdersOccupiedAmount";
export var InputAmountStatus;

(function (InputAmountStatus) {
  InputAmountStatus["DEFAULT"] = "default";
  InputAmountStatus["VERIFIED"] = "verified";
  InputAmountStatus["MIN_AMOUNT_ERROR"] = "min_amount_error";
  InputAmountStatus["MAX_AMOUNT_ERROR"] = "max_amount_error";
  InputAmountStatus["BALANCE_OCCUPIED_ERROR"] = "balance_occupied_error";
})(InputAmountStatus || (InputAmountStatus = {}));

export var ExpirationStatus;

(function (ExpirationStatus) {
  ExpirationStatus["DEFAULT"] = "default";
  ExpirationStatus["VERIFIED"] = "verified";
  ExpirationStatus["MAX_EXPIRATION_ERROR"] = "max_expiration_error";
})(ExpirationStatus || (ExpirationStatus = {}));

export var useAllowanceVerify = () => {
  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useBestBuyInputAmoun = useBestBuyInputAmount(),
      inputAmount = _useBestBuyInputAmoun.inputAmount;

  var _useBestBuyPair = useBestBuyPair(),
      _useBestBuyPair2 = _slicedToArray(_useBestBuyPair, 1),
      inputToken = _useBestBuyPair2[0];

  var allowance = useBestBuyAllowance(inputToken);
  var chainId = useUpdaterChainId();
  var bestBuyAddress = BEST_BUY[chainId];
  var activeOrdersOccupiedAmount = useActiveOrdersOccupiedAmount(inputToken);

  var _useState = useState(false),
      needCancelApprove = _useState[0],
      setNeedCancelApprove = _useState[1];

  var _useApproveCallback = useApproveCallback(inputToken, MaxUint256, bestBuyAddress),
      _useApproveCallback2 = _slicedToArray(_useApproveCallback, 2),
      approvalState = _useApproveCallback2[0],
      approve = _useApproveCallback2[1];

  var _useApproveCallback3 = useApproveCallback(inputToken, Zero, bestBuyAddress),
      _useApproveCallback4 = _slicedToArray(_useApproveCallback3, 2),
      cancelApprovalState = _useApproveCallback4[0],
      cancelApprove = _useApproveCallback4[1];

  var decimals = inputToken === null || inputToken === void 0 ? void 0 : inputToken.decimals;
  var modalState = useMemo(() => {
    return {
      token: inputToken,
      progress: needCancelApprove ? ApprovalProgress.CANCEL_APPROVING : ApprovalProgress.APPROVING,
      approvalState: needCancelApprove ? ApprovalState.APPROVED : ApprovalState.NOT_APPROVED,
      closeModalAfterApprovalConfirmed: true
    };
  }, [inputToken, needCancelApprove]);
  var isApproved = useMemo(() => allowance && !allowance.isZero(), [allowance]);
  var toggleApprovalModal = useModalToggle(ApplicationModal.APPROVAL_STATUS, {
    stack: true,
    modalState
  });
  var needReduceAllowance2Zero = useNeedReduceAllowance2Zero(inputToken, bestBuyAddress);
  useEffect(() => {
    needReduceAllowance2Zero().then(result => {
      setNeedCancelApprove(result);
    });
  }, [inputToken, bestBuyAddress, approvalState, cancelApprovalState, needReduceAllowance2Zero]);
  return useCallback(() => {
    if (approvalState === ApprovalState.APPROVED) return true;
    if (!allowance) return false;
    var formattedAllowance = toBigNumberWithDecimal(allowance, decimals);
    var formattedActiveOrdersOccupiedAmount = toBigNumberWithDecimal(activeOrdersOccupiedAmount, decimals);
    var needApproveOrReApprove = new FixedPointNumber(inputAmount, decimals).gt(formattedAllowance.minus(formattedActiveOrdersOccupiedAmount));

    if (needApproveOrReApprove) {
      if (isApproved && needCancelApprove) {
        message.success(t('token_allowance_not_enough_and_need_cancel', {
          token: inputToken.symbol
        }));
        toggleApprovalModal();
        cancelApprove();
      } else {
        if (isApproved) {
          message.success(t('token_allowance_not_enough', {
            token: inputToken.symbol
          }));
        }

        toggleApprovalModal();
        approve();
      }

      return false;
    }

    return true;
  }, [inputAmount, inputToken, allowance, approvalState, isApproved, needCancelApprove, toggleApprovalModal, approve, cancelApprove, activeOrdersOccupiedAmount]);
};

var useNumberVerify = () => {
  return useCallback(value => {
    if (value === '00') return false;
    var reg = new RegExp(/^\d*$/);

    if (!reg.test(value) || value.trim().length === 0) {
      return false;
    }

    return true;
  }, []);
};

export var useMinMaxAmountVerify = () => {
  var _useNetworkValidator = useNetworkValidator(),
      isArbitrum = _useNetworkValidator.isArbitrum;

  return useCallback((amount, expiration) => {
    var minAmount = isArbitrum ? 10 : 100;
    var maxAmount = 500000;
    var calcAmount = Number(amount) / Number(expiration);

    if (calcAmount < minAmount) {
      return InputAmountStatus.MIN_AMOUNT_ERROR;
    }

    if (amount > maxAmount) {
      return InputAmountStatus.MAX_AMOUNT_ERROR;
    }

    return InputAmountStatus.VERIFIED;
  }, [isArbitrum]);
};
export var useAmountVerify = () => {
  var _useBestBuyInputAmoun2 = useBestBuyInputAmount(),
      inputAmount = _useBestBuyInputAmoun2.inputAmount;

  var numberVerify = useNumberVerify();
  var verifyMinMaxAmount = useMinMaxAmountVerify();

  var _useBestBuyUserConfig = useBestBuyUserConfig(),
      expiration = _useBestBuyUserConfig.expiration;

  var _expiration$split = expiration.split('_'),
      _expiration$split2 = _slicedToArray(_expiration$split, 1),
      expirationValue = _expiration$split2[0];

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  return useCallback(() => {
    if (!numberVerify(inputAmount)) {
      message.error(t('unvalid_input_amount'));
      return false;
    }

    if (verifyMinMaxAmount(inputAmount, expirationValue) !== 'verified') {
      return false;
    }

    return true;
  }, [inputAmount, expiration, numberVerify]);
};
export var useBalanceVerify = () => {
  var _useBestBuyInputAmoun3 = useBestBuyInputAmount(),
      inputAmount = _useBestBuyInputAmoun3.inputAmount;

  var _useBestBuyPair3 = useBestBuyPair(),
      _useBestBuyPair4 = _slicedToArray(_useBestBuyPair3, 1),
      inputToken = _useBestBuyPair4[0];

  var _useWeb3React = useWeb3React(),
      account = _useWeb3React.account;

  var balance = useTokenBalance(account, inputToken);

  var _useTranslation3 = useTranslation(),
      t = _useTranslation3.t;

  return useCallback(() => {
    var isInsufficient = parseUnits(inputAmount, inputToken.decimals).gt(balance === null || balance === void 0 ? void 0 : balance.toFixed(0));

    if (isInsufficient) {
      message.error(t('$symbol_insufficient_balance', {
        symbol: inputToken.symbol
      }));
      return false;
    } else return true;
  }, [balance, inputAmount, inputToken]);
};
export var useExpirationVerify = () => {
  var _useBestBuyUserConfig2 = useBestBuyUserConfig(),
      expiration = _useBestBuyUserConfig2.expiration;

  var _expiration$split3 = expiration.split('_'),
      _expiration$split4 = _slicedToArray(_expiration$split3, 1),
      expirationValue = _expiration$split4[0];

  var maxExpiration = 1460;
  return useCallback(() => {
    var isExceededMaxPeriod = Number(expirationValue) > maxExpiration;

    if (isExceededMaxPeriod) {
      return ExpirationStatus.MAX_EXPIRATION_ERROR;
    } else return ExpirationStatus.VERIFIED;
  }, [expiration]);
};
export var useActiveBalanceVerify = () => {
  var _useBestBuyInputAmoun4 = useBestBuyInputAmount(),
      inputAmount = _useBestBuyInputAmoun4.inputAmount;

  var _useBestBuyPair5 = useBestBuyPair(),
      _useBestBuyPair6 = _slicedToArray(_useBestBuyPair5, 1),
      inputToken = _useBestBuyPair6[0];

  var _useWeb3React2 = useWeb3React(),
      account = _useWeb3React2.account;

  var availableBalance = useAvailableTokenBalance(account, inputToken);

  var _useTranslation4 = useTranslation(),
      t = _useTranslation4.t;

  return useCallback(() => {
    if (new FixedPointNumber(inputAmount, inputToken.decimals).gt(availableBalance.toDecimalsFixed())) {
      return InputAmountStatus.BALANCE_OCCUPIED_ERROR;
    }

    return InputAmountStatus.VERIFIED;
  }, [availableBalance, inputAmount, t, inputToken]);
};
export var useTradeVerify = () => {
  var verifyAmount = useAmountVerify();
  var verifyAllowance = useAllowanceVerify();
  var verifyBalance = useBalanceVerify();
  var verifyExpiration = useExpirationVerify();
  var verifyActiveBalance = useActiveBalanceVerify();
  return useCallback(() => {
    if (verifyAmount() && verifyBalance() && verifyActiveBalance() === InputAmountStatus.VERIFIED && verifyAllowance() && verifyExpiration() === ExpirationStatus.VERIFIED) {
      return true;
    }

    return false;
  }, [verifyAmount, verifyAllowance]);
};